import * as React from 'react';
import { useQuery } from 'react-query';
import { useSetState } from 'react-use';
import { useTranslation } from 'react-i18next';
import { Icon, Select, Space } from 'ebs-design';
import { CustomPeriod, Filters, PeriodType } from 'components/molecules';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { Calendar, TaskList, UserAlone } from 'resources/icons';
import { dateFormat, dateFormatAPI, format } from 'libs';
import { useTaskTypesAPI, useUsersAPI } from 'api';
import { defaultNonPagination, getPeriodByName } from 'utils';
import { List, OutstandingDaysType, Results, TaskStatusFilterType, User } from 'types';

interface TaskFilterProps {
  count?: number;
  rightComponents?: React.ReactNode;
}

interface FiltersProps {
  search?: string;
  owner?: string;
  created_by?: string;
  type?: string;
  completed?: string;
  deadline?: string;
  deadline_start?: string;
  deadline_end?: string;
  type_prefix?: string;
  page?: number;
}

export const TaskFilter: React.FC<TaskFilterProps> = ({ count, rightComponents }) => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const { getUsers } = useUsersAPI();
  const { getAllTaskTypes } = useTaskTypesAPI();
  const { updateQuery } = useQueryUpdate();
  const [taskTypeFilter, setTaskTypeFilter] = useSetState(defaultNonPagination);
  const [usersFilter, setUsersFilter] = useSetState(defaultNonPagination);
  const [openCalendarPicker, setOpenCalendarPicker] = React.useState(false);

  const [filters, setFilters] = useSetState<FiltersProps>(params);

  const { data: users } = useQuery<Results<User>>(['users', { ...usersFilter }], getUsers);
  const { data: listTaskTypes } = useQuery<Results<List>>(
    ['list-task-type', { ...taskTypeFilter }],
    getAllTaskTypes,
    {
      select: (data) => ({
        ...data,
        results: [
          ...data.results,
          {
            key: OutstandingDaysType.OUTSTANDING_DAYS,
            label: t(`contracts.outstandingDays`),
          },
        ],
      }),
    },
  );

  React.useEffect(() => {
    updateQuery({ ...filters, per_page: params.per_page });
  }, [filters]);

  const handleFilters = (value): void => {
    if (value?.deadline !== undefined) {
      if (value?.deadline === 'custom') {
        setOpenCalendarPicker(true);
      } else {
        const [deadline_start, deadline_end] = getPeriodByName(value.deadline);
        setFilters({ deadline_start, deadline_end, ...value, page: 1 });
      }
    } else if ('deadline' in value) {
      setFilters({ deadline_start: undefined, deadline_end: undefined, ...value, page: 1 });
    }
    if ('type' in value && value?.type !== OutstandingDaysType.OUTSTANDING_DAYS) {
      setFilters({ type_prefix: undefined, type: value?.type, page: 1 });
    }
    if (value?.type === OutstandingDaysType.OUTSTANDING_DAYS) {
      setFilters({ type_prefix: value.type, type: undefined, page: 1 });
    } else {
      setFilters({ ...value, page: 1 });
    }
  };

  const handleCustomPeriod = (deadline_start: string, deadline_end: string): void => {
    setFilters({ deadline_start, deadline_end, deadline: 'custom', page: 1 });
    setOpenCalendarPicker(false);
  };

  const statusOptions = React.useMemo(
    () => [
      {
        label: t('filter.uncompleted'),
        value: TaskStatusFilterType.UNCOMPLETED,
      },
      { label: t('filter.completed'), value: TaskStatusFilterType.COMPLETED },
      { label: t('filter.autoCompleted'), value: TaskStatusFilterType.AUTO_COMPLETED },
      { label: t('filter.all'), value: TaskStatusFilterType.ALL },
    ],
    [t],
  );

  return (
    <>
      {openCalendarPicker && (
        <CustomPeriod
          onClose={() => setOpenCalendarPicker(false)}
          onChange={handleCustomPeriod}
          initialValue={
            [
              params.deadline_start && format(params.deadline_start, dateFormatAPI, dateFormat),
              params.deadline_end && format(params.deadline_end, dateFormatAPI, dateFormat),
            ] as PeriodType
          }
        />
      )}
      <Filters className="mb-0" onChange={handleFilters}>
        <Space justify="start">
          <Space>
            <h3 className="title">{`${t(`task.task`)}${count ? ` (${count})` : ``}`}</h3>
          </Space>
          <Filters.Search field="search" value={params.search} placeholder={t('filter.search')} />
        </Space>
        <Space justify="end" wrap>
          <Filters.Select
            prefix={<Icon component={UserAlone} />}
            placeholder={t('filter.agent')}
            value={params.owner}
            field="owner"
          >
            <Select.Options>
              {(users?.results || []).map((item, key) => (
                <Select.Options.Item key={`${key}-user`} value={item.id}>
                  {item?.first_name || item?.last_name
                    ? `${item?.first_name} ${item?.last_name}`
                    : item.email}
                </Select.Options.Item>
              ))}
            </Select.Options>
            <Select.Pagination
              count={users?.count || 0}
              limit={usersFilter.per_page}
              page={usersFilter.page}
              setPage={(page) => setUsersFilter({ page })}
              mode="scroll"
            />
          </Filters.Select>

          <Filters.Select
            prefix={<Icon component={UserAlone} />}
            placeholder={t('task.taskAuthor')}
            value={params.created_by}
            field="created_by"
          >
            <Select.Options>
              {(users?.results || []).map((item, key) => (
                <Select.Options.Item key={`${key}-user`} value={item.id}>
                  {item?.first_name || item?.last_name
                    ? `${item?.first_name} ${item?.last_name}`
                    : item.email}
                </Select.Options.Item>
              ))}
            </Select.Options>
            <Select.Pagination
              count={users?.count || 0}
              limit={usersFilter.per_page}
              page={usersFilter.page}
              setPage={(page) => setUsersFilter({ page })}
              mode="scroll"
            />
          </Filters.Select>
          <Filters.Select
            prefix={<Icon component={TaskList} />}
            placeholder={t('filter.type')}
            value={(params?.type && parseInt(params.type)) || params?.type_prefix}
            field="type"
          >
            <Select.Options>
              {(listTaskTypes?.results || [])?.map((item, key) => (
                <Select.Options.Item key={`${key}-list-task-type`} value={item.key}>
                  {item.label}
                </Select.Options.Item>
              ))}
            </Select.Options>

            <Select.Pagination
              count={listTaskTypes?.count || 0}
              limit={taskTypeFilter.per_page}
              page={taskTypeFilter.page}
              setPage={(page) => setTaskTypeFilter({ page })}
              mode="scroll"
            />
          </Filters.Select>
          <Filters.Select
            className="created-at-drop-list"
            prefix={<Icon component={Calendar} />}
            placeholder={t('filter.deadline')}
            field="deadline"
            value={params.deadline}
          >
            <Select.Options>
              <Select.Options.Item value="today">{t('filter.today')}</Select.Options.Item>
              <Select.Options.Item value="week">{t('filter.this_week')}</Select.Options.Item>
              <Select.Options.Item value="month">{t('filter.this_month')}</Select.Options.Item>
              <Select.Options.Item value="quarter">{t('filter.this_quarter')}</Select.Options.Item>
              <Select.Options.Item value="year">{t('filter.this_year')}</Select.Options.Item>
              <Select.Options.Item value="yesterday" className="period-drop-list__separator">
                {t('filter.yesterday')}
              </Select.Options.Item>
              <Select.Options.Item value="previous_week">
                {t('filter.previous_week')}
              </Select.Options.Item>
              <Select.Options.Item value="previous_month">
                {t('filter.previous_month')}
              </Select.Options.Item>
              <Select.Options.Item value="previous_year">
                {t('filter.previous_year')}
              </Select.Options.Item>
              <Select.Options.Item value="all_time">{t('filter.all_time')}</Select.Options.Item>
              <Select.Options.Item
                value="custom"
                className="period-drop-list__custom period-drop-list__separator"
              >
                {t('filter.custom')}
              </Select.Options.Item>
            </Select.Options>
          </Filters.Select>

          <Filters.ButtonGroup
            options={statusOptions}
            field="completed"
            value={params.completed || TaskStatusFilterType.ALL}
          />
          {rightComponents}
        </Space>
      </Filters>
    </>
  );
};
